import { Component, Output, EventEmitter, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TeamService } from '../../core/services/team.service';
import { CreateInvitationDto, MembershipRole } from '../../core/models/team.model';
import { CustomClaimService } from '../../core/services/custom-claim.service';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

/**
 * Invite Member Modal Component
 *
 * Modal for inviting new members to the organization.
 * Supports role selection (Pilot, PIC, or both) with validation.
 *
 * Reference: Task Group 8 - Team Management UI - Invite Member Flow
 * Spec: /agent-os/specs/2026-01-22-user-onboarding/planning/spec.md
 */
@Component({
  selector: 'app-invite-member-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MaxLengthDirective],
  templateUrl: './invite-member-modal.component.html',
  styleUrls: ['./invite-member-modal.component.scss']
})
export class InviteMemberModalComponent {
  private teamService = inject(TeamService);
  private customClaimService = inject(CustomClaimService);

  @Output() invitationCreated = new EventEmitter<void>();

  // Modal state
  isOpen = signal(false);
  isProcessing = signal(false);

  // Form data
  email = signal('');
  firstName = signal('');
  lastName = signal('');
  isPilot = signal(false);
  isPilotInCharge = signal(false);

  // Validation errors
  emailError = signal<string | null>(null);
  roleError = signal<string | null>(null);
  apiError = signal<string | null>(null);

  // Current organization
  private currentOrganizationId = signal<string | null>(null);

  /**
   * Open the modal
   */
  open(organizationId: string): void {
    this.currentOrganizationId.set(organizationId);
    this.resetForm();
    this.isOpen.set(true);
  }

  /**
   * Close the modal
   */
  close(): void {
    if (this.isProcessing()) return;
    this.isOpen.set(false);
    this.resetForm();
  }

  /**
   * Reset form to initial state
   */
  private resetForm(): void {
    this.email.set('');
    this.firstName.set('');
    this.lastName.set('');
    this.isPilot.set(false);
    this.isPilotInCharge.set(false);
    this.clearErrors();
  }

  /**
   * Clear all validation errors
   */
  private clearErrors(): void {
    this.emailError.set(null);
    this.roleError.set(null);
    this.apiError.set(null);
  }

  /**
   * Validate form
   */
  private validateForm(): boolean {
    this.clearErrors();
    let isValid = true;

    // Validate email
    const emailValue = this.email().trim();
    if (!emailValue) {
      this.emailError.set('Email is required');
      isValid = false;
    } else if (!this.isValidEmail(emailValue)) {
      this.emailError.set('Please enter a valid email address');
      isValid = false;
    }

    // Validate roles (at least one must be selected)
    if (!this.isPilot() && !this.isPilotInCharge()) {
      this.roleError.set('Please select at least one role');
      isValid = false;
    }

    return isValid;
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Calculate role bitwise value
   */
  private calculateRole(): number {
    let role = 0;
    if (this.isPilot()) {
      role |= MembershipRole.Pilot;
    }
    if (this.isPilotInCharge()) {
      role |= MembershipRole.PilotInCharge;
    }
    return role;
  }

  /**
   * Handle form submission
   */
  async onSubmit(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }

    const organizationId = this.currentOrganizationId();
    if (!organizationId) {
      this.apiError.set('Organization not found');
      return;
    }

    this.isProcessing.set(true);
    this.clearErrors();

    try {
      const dto: CreateInvitationDto = {
        email: this.email().trim(),
        firstName: this.firstName().trim(),
        lastName: this.lastName().trim(),
        role: this.calculateRole()
      };

      await this.teamService.createInvitation(organizationId, dto);

      // Success - close modal and emit event
      this.isOpen.set(false);
      this.invitationCreated.emit();
      this.resetForm();
    } catch (error: any) {
      // Display API error
      if (error.error?.error) {
        this.apiError.set(error.error.error);
      } else if (error.message) {
        this.apiError.set(error.message);
      } else {
        this.apiError.set('Failed to send invitation. Please try again.');
      }
    } finally {
      this.isProcessing.set(false);
    }
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.close();
    }
  }

  /**
   * Handle escape key
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.isProcessing()) {
      this.close();
    }
  }
}
